import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Sun,
  Moon,
  User,
  LogOutIcon,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "../context/AuthProvider";
import { useTheme } from "../context/ThemeContext";
import Loader from "./Loader";
import NotificationBell from "./NotificationBell";

const Header = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isDarkMode, toggleTheme } = useTheme();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const isStudent = user?.role === "student";

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const handleLogout = () => {
    logout(); // clears user + localStorage
    navigate("/login"); // redirect to login
  };

  return (
    <header className="bg-indigo-600 dark:bg-indigo-800 text-white shadow-md flex justify-between items-center px-4 py-3 md:px-6">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-3">
          <img
            src="/assets/images/bhit-logo.png"
            alt="BHIT Logo"
            className="h-8 w-auto filter brightness-0 invert"
          />
          <span className="text-lg font-bold">
            {isStudent ? "Student Portal" : "Admin Panel"}
          </span>
        </div>
      </div>

      {loading && <Loader />}

      <div className="flex items-center gap-4">
        <button
          onClick={toggleTheme}
          className="p-2 rounded-full hover:bg-indigo-700 dark:hover:bg-indigo-600"
          title={`Switch to ${isDarkMode ? "light" : "dark"} mode`}
        >
          {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
        </button>

        {/* Notification Bell */}
        <NotificationBell />

        {/* Profile dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center space-x-2 bg-indigo-500 px-3 py-2 rounded-full hover:bg-indigo-700"
          >
            <User size={20} />
            <span className="hidden md:inline text-sm font-medium">
              {user?.name || user?.role}
            </span>
          </button>
          {dropdownOpen && (
            <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 shadow-lg rounded-md z-50">
              <Link
                to="/profile"
                className="flex items-center gap-2 text-gray-800 dark:text-white px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 no-underline"
                onClick={() => setDropdownOpen(false)}
              >
                <User size={16} /> Profile
              </Link>
              <button
                className="w-full rounded-md flex items-center gap-2 text-left text-gray-800 dark:text-white px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={handleLogout}
              >
                <LogOutIcon size={16} /> Logout
              </button>
            </div>
          )}
        </div>

        <button
          className="md:hidden p-2 rounded hover:bg-indigo-700"
          onClick={toggleSidebar}
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
    </header>
  );
};

export default Header;
